import { LogicType } from '@webapp/common/resources/survey';

import type { QuestionAnswerActor } from '@webapp/survey/src/entities/answer-machine';
import type { BundleEntity } from '@webapp/survey/src/entities/bundle';
import type { QuotaClaim } from '@webapp/survey/src/machine/api';
import { isQuotaLogicMatched } from '@webapp/survey/src/machine/helpers/logic-new';
import type { QuestionActorRef } from '@webapp/survey/src/machine/question-machine';

import { isQuotaHideLogic } from './quota-action';

export { findQuotaLogicById, isQuotaHideLogic, resolveQuotaAction } from './quota-action';

export interface BundleQuotaClaims {
    claims: Array<QuotaClaim>;
    signature: string;
}

// Собирает claim-запрос по квотным вопросам страницы: по каждому вопросу —
// id выбранных ответов, попавших под его квотные логики. Вопрос включается в
// запрос всегда, даже без совпадений: так сервис квот понимает, что с него
// ушли (поменяли ответ/сняли выбор) и снимает прежнюю бронь.
//
// signature — снимок набора ответов: если он не изменился, повторный сабмит
// не должен заново ходить в сервис квот (идемпотентность на клиенте).
export const collectBundleQuotaClaims = (bundle: BundleEntity | null | undefined): BundleQuotaClaims => {
    if (!bundle) {
        return { claims: [], signature: '' };
    }

    const claims: Array<QuotaClaim> = [];

    bundle.questions.forEach((question) => {
        const context = question.getSnapshot().context;
        const quotaLogics = context.logics.filter((logic) => logic.type === LogicType.TYPE_QUESTION_QUOTA);

        if (quotaLogics.length === 0) {
            return;
        }

        const answerIds = new Set<number>();
        quotaLogics.forEach((logic) => {
            if (isQuotaLogicMatched(logic, question)) {
                logic.params.forEach((param) => answerIds.add(Number(param.id)));
            }
        });

        claims.push({
            questionId: context.id,
            answerIds: [...answerIds].sort((a, b) => a - b),
        });
    });

    const signature = JSON.stringify(claims.map((claim) => [claim.questionId, claim.answerIds]));

    return { claims, signature };
};

// Упорядоченный список claim'ов по всем ранее отвеченным квотным вопросам —
// для повторного бронирования при возврате в сохранённое прохождение.
// Порядок = порядок бандлов/вопросов, именно он задаёт точку отката: сервис
// квот возвращает первый по этому порядку отказ.
export const collectResumeQuotaClaims = (bundles: Array<BundleEntity>): Array<QuotaClaim> => {
    const claims: Array<QuotaClaim> = [];
    const seen = new Set<number>();

    bundles.forEach((bundle) => {
        collectBundleQuotaClaims(bundle).claims.forEach((claim) => {
            if (claim.answerIds.length === 0 || seen.has(claim.questionId)) {
                return;
            }
            seen.add(claim.questionId);
            claims.push(claim);
        });
    });

    return claims;
};

// Сбрасывает ответы начиная с бандла fromIndex: сам вопрос-виновник и весь
// хвост. Возвращает id затронутых вопросов.
export const resetTailQuestions = (bundles: Array<BundleEntity>, fromIndex: number): Array<number> => {
    const affected: Array<number> = [];

    bundles.slice(fromIndex).forEach((bundle) => {
        bundle.questions.forEach((question) => {
            const context = question.getSnapshot().context;
            affected.push(context.id);

            context.responseActor?.send({ type: 'CLEAR' });
            context.answersActors?.forEach((answerActor) => {
                answerActor.getSnapshot().context.response?.send({ type: 'CLEAR' });
                answerActor.getSnapshot().context.answeredCount = 0;
                answerActor.getSnapshot().context.valid = true;
            });
            context.groupsActors?.forEach((groupActor) => {
                const responses = groupActor.getSnapshot().context.responses || {};
                Object.values(responses).forEach((responseActor) => responseActor.send({ type: 'CLEAR' }));
            });

            context.clicks = [];
            context.files = [];
            context.invalid = false;
            context.valid = true;
            context.dirty = false;
        });
    });

    return affected;
};

// Скрывает варианты закрытых квотных вопросов. Возвращает true, если что-то
// скрыли или сняли.
//
// keepSelected — режим heartbeat-синхронизации: уже выбранный вариант не прячем
// и не сбрасываем. Так респондент видит свой выбор до сабмита, а отказ приходит
// на claim (action = ANSWERS_HIDE): показывается ошибка, вариант скрывается и
// снимается — можно выбрать другой. Без этого флага (отказ claim) вариант
// прячется сразу вместе со снятием выбора.
const hideAnswersOfLogic = (
    question: QuestionActorRef,
    logic: { id: number; params: Array<{ id: number }> },
    keepSelected = false,
): boolean => {
    let changed = false;
    const answerIds = logic.params.map((p) => p.id);

    question.getSnapshot().context.answersActors.forEach((answerActor: QuestionAnswerActor) => {
        const answerSnapshot = answerActor.getSnapshot();
        if (!answerIds.includes(answerSnapshot.context.id) || answerSnapshot.context.hidden) {
            return;
        }

        const isAnswered = !!answerSnapshot.context.response?.getSnapshot().context.value;

        if (keepSelected && isAnswered) {
            return;
        }

        console.log('[quota] hide closed answer', {
            questionId: question.getSnapshot().context.id,
            logicId: logic.id,
            answerId: answerSnapshot.context.id,
            isAnswered,
        });
        if (isAnswered) {
            answerSnapshot.context.response?.send({ type: 'CLEAR' });
            answerActor.send({ type: 'SET_ANSWERED_COUNT', value: 0 });
        }
        answerActor.send({ type: 'SET_QUOTA_HIDDEN', value: true });
        changed = true;
    });

    return changed;
};

// Возвращает «отпущенный» вариант в UI: снимаем только квотное скрытие,
// чужие скрытия (логика бандлов, переносы ответов) не трогаем.
const unhideAnswersOfLogic = (
    question: QuestionActorRef,
    logic: { id: number; params: Array<{ id: number }> },
): boolean => {
    let changed = false;
    const answerIds = logic.params.map((p) => p.id);

    question.getSnapshot().context.answersActors.forEach((answerActor: QuestionAnswerActor) => {
        const answerSnapshot = answerActor.getSnapshot();
        if (answerIds.includes(answerSnapshot.context.id) && answerSnapshot.context.quotaHidden) {
            answerActor.send({ type: 'SET_QUOTA_HIDDEN', value: false });
            changed = true;
        }
    });

    return changed;
};

// Скрытие/возврат вариантов — только для квот с действием ANSWERS_HIDE.
// У дисквала/завершения вариант остаётся видимым: действие применяется
// в момент выбора (claim denied), а не скрытием.
const quotaLogicsOf = (question: QuestionActorRef) =>
    question
        .getSnapshot()
        .context.logics.filter((logic) => logic.type === LogicType.TYPE_QUESTION_QUOTA && isQuotaHideLogic(logic));

// Выбран ли на вопросе ответ, попадающий под квотную логику.
const isAnsweredInLogic = (question: QuestionActorRef, logic: { params: Array<{ id: number }> }): boolean => {
    const answerIds = new Set(logic.params.map((p) => p.id));

    return question.getSnapshot().context.answersActors.some((answerActor: QuestionAnswerActor) => {
        const answer = answerActor.getSnapshot().context;
        return answerIds.has(answer.id) && !!answer.response?.getSnapshot().context.value;
    });
};

export interface QuotaHideRollbackTarget {
    bundleIndex: number;
    questionId: number;
    logicId: number;
}

// Ищет самый ранний вопрос, на котором респондент уже выбрал вариант, попавший
// под закрытую ANSWERS_HIDE-квоту: туда нужно откатиться, сбросив хвост.
// Нужно, когда квота набралась во время прохождения (quotaExhausted): вместо
// завершения опроса возвращаем респондента к вопросу и просим выбрать другой
// ответ.
export const findQuotaHideRollback = (
    bundles: Array<BundleEntity>,
    closedLogicIds: Array<number>,
): QuotaHideRollbackTarget | null => {
    const closed = new Set(closedLogicIds);
    let target: QuotaHideRollbackTarget | null = null;

    bundles.forEach((bundle, bundleIndex) => {
        bundle.questions.forEach((question) => {
            const context = question.getSnapshot().context;

            quotaLogicsOf(question).forEach((logic) => {
                if (!closed.has(logic.id) || !isAnsweredInLogic(question, logic)) {
                    return;
                }
                if (!target || bundleIndex < target.bundleIndex) {
                    target = { bundleIndex, questionId: context.id, logicId: logic.id };
                }
            });
        });
    });

    return target;
};

// Есть ли среди закрытых квот хотя бы одна ANSWERS_HIDE-логика опроса: если да,
// завершать прохождение по quotaExhausted нельзя — респондент может выбрать
// другой вариант (вариант уже скрыт/скроется).
export const hasQuotaHideLogic = (questions: Array<QuestionActorRef>, closedLogicIds: Array<number>): boolean => {
    const closed = new Set(closedLogicIds);

    return questions.some((question) => quotaLogicsOf(question).some((logic) => closed.has(logic.id)));
};

// Полная синхронизация скрытия по свежему снапшоту сервиса квот: закрытые
// логики прячем, открывшиеся — показываем. Снапшот приходит целиком
// (closedAnswers), поэтому важно именно синхронизировать, а не только прятать:
// при овербукинге брони истекают, и варианты должны возвращаться.
export const syncQuotaHides = (questions: Array<QuestionActorRef>, closedLogicIds: Array<number>): boolean => {
    const closed = new Set(closedLogicIds);
    let changed = false;

    questions.forEach((question) => {
        quotaLogicsOf(question).forEach((logic) => {
            const logicChanged = closed.has(logic.id)
                ? hideAnswersOfLogic(question, logic, true)
                : unhideAnswersOfLogic(question, logic);
            changed = logicChanged || changed;
        });
    });

    return changed;
};

// Точечное скрытие по одной логике — для отказа claim (гонка: вариант
// закрылся между снапшотом и сабмитом). Снапшот целиком тут не подходит,
// иначе можно разскрыть чужие ещё закрытые варианты.
export const hideQuotaAnswers = (questions: Array<QuestionActorRef>, logicId: number): boolean => {
    let changed = false;

    questions.forEach((question) => {
        quotaLogicsOf(question)
            .filter((logic) => logic.id === logicId)
            .forEach((logic) => {
                changed = hideAnswersOfLogic(question, logic) || changed;
            });
    });

    return changed;
};
